import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchAttachmentAsFile } from 'attachment/api/utils';
import { logger } from 'common/services';
import { EventImageParams, ImageType } from 'domain/event';
import { conformFormImages, filterOutEmptyAndAlreadyUploadedImages, mapImageToApi } from 'event/mappers';
import { union } from 'lodash-es';
import find from 'lodash/fp/find';
import flow from 'lodash/fp/flow';
import prop from 'lodash/fp/prop';
import { EventWizardFormSerializedValues } from 'pages/EventWizard/validation';
import { eventApi, eventSeriesApi } from 'store/apis/event';

export const updateImages = createAsyncThunk<
  number[],
  { eventId: number; seriesId?: number; values: EventWizardFormSerializedValues }
>('[Event Wizard] Update event questions', async ({ eventId, seriesId, values }, { dispatch }) => {
  const { eventImage, sponsors, defaultEventImage } = values;
  const selectedImages = conformFormImages({ eventImage, sponsors });
  const imagesToUpload: EventImageParams[] = filterOutEmptyAndAlreadyUploadedImages(selectedImages).map(mapImageToApi);
  const isEventImageUploaded = !!eventImage?.id;

  if (!isEventImageUploaded && !find({ imageType: ImageType.EventImage })(imagesToUpload) && defaultEventImage) {
    try {
      const image = await fetchAttachmentAsFile(defaultEventImage);
      imagesToUpload.push({
        image,
        imageType: ImageType.EventImage,
      });
    } catch (error) {
      // The default event image is a non-critical fallback. If it can't be fetched
      // (e.g. CORS/network — see N2-2766), skip it rather than rejecting the whole
      // save, which would silently drop the user's other edits (event details,
      // questions, tag categories).
      logger.error('Failed to fetch default event image; saving without it', { eventId }, error);
    }
  }

  const apiCalls = imagesToUpload.map((imageObj) => {
    if (seriesId) {
      return dispatch(eventSeriesApi.endpoints.postEventSeriesImage.initiate({ seriesId, ...imageObj })).unwrap();
    }
    return dispatch(eventApi.endpoints.postEventImage.initiate({ id: eventId, ...imageObj })).unwrap();
  });

  const uploadedImages = await Promise.all(apiCalls);

  const alreadyUploadedImageIds = selectedImages.map(flow(prop('image'), prop('id'))).filter(Boolean) as number[];
  const newImageIds = uploadedImages.map(prop('id')) as number[];
  return union(alreadyUploadedImageIds, newImageIds);
});
